import * as React from 'react';
import { TextField, type TextFieldProps } from '@mui/material';
import { formatNumberItalian, parseNumberFromItalian } from '../../utils/numberFormat';

export interface ItalianNumberFieldProps
  extends Omit<TextFieldProps, 'value' | 'onChange' | 'type'> {
  /** Valore numerico "vero" (non la stringa formattata mostrata nell'input). */
  value: number | null | undefined;
  /** Notifica il nuovo valore numerico (o `null` se il campo è vuoto/non valido). */
  onChange: (value: number | null) => void;
  /** Numero di decimali mostrati quando il campo non è a fuoco (default: 0, es. importi interi). */
  decimalPlaces?: number;
}

/**
 * TextField numerico con formattazione italiana (punto delle migliaia, virgola decimale).
 *
 * A differenza di un `TextField type="number"`, mostra sempre il separatore delle migliaia
 * quando il campo non è a fuoco, e permette comunque di digitare liberamente durante l'editing.
 */
export const ItalianNumberField = React.forwardRef<HTMLInputElement, ItalianNumberFieldProps>(
  ({ value, onChange, decimalPlaces = 0, onFocus, onBlur, ...textFieldProps }, ref) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const [text, setText] = React.useState<string>(() =>
      value != null ? formatNumberItalian(value, { maximumFractionDigits: decimalPlaces }) : ''
    );

    // Quando il valore esterno cambia (es. caricamento dati, reset del form) e il campo non è
    // a fuoco, risincronizza il testo mostrato con il valore "vero".
    React.useEffect(() => {
      if (isFocused) return;
      setText(value != null ? formatNumberItalian(value, { maximumFractionDigits: decimalPlaces }) : '');
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, decimalPlaces, isFocused]);

    return (
      <TextField
        {...textFieldProps}
        inputRef={ref}
        value={text}
        onFocus={event => {
          setIsFocused(true);
          onFocus?.(event);
        }}
        onChange={event => {
          // Consenti solo cifre, punto (migliaia), virgola (decimale) e segno meno iniziale.
          const sanitized = event.target.value.replace(/[^0-9.,-]/g, '');
          setText(sanitized);
          onChange(parseNumberFromItalian(sanitized));
        }}
        onBlur={event => {
          setIsFocused(false);
          const parsed = parseNumberFromItalian(text);
          setText(parsed != null ? formatNumberItalian(parsed, { maximumFractionDigits: decimalPlaces }) : '');
          onChange(parsed);
          onBlur?.(event);
        }}
        inputProps={{ inputMode: 'decimal', ...textFieldProps.inputProps }}
      />
    );
  }
);

ItalianNumberField.displayName = 'ItalianNumberField';
